import { useEffect, useRef, useState } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ConfigProvider, AdaptivityProvider, AppRoot, Spinner, Button, Headline, Text } from '@vkontakte/vkui';
import { Icon24ChevronLeft } from '@vkontakte/icons';
import '@vkontakte/vkui/dist/vkui.css';
import { useAuthStore } from './stores/auth';
import { useWeddingStore } from './stores/wedding';
import { api } from './api/client';
import AuthPage from './pages/Auth/AuthPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import GuestsPage from './pages/Guests/GuestsPage';
import TasksPage from './pages/Tasks/TasksPage';
import TaskDetailPage from './pages/Tasks/TaskDetailPage';
import ChecklistAnalytics from './pages/Tasks/ChecklistAnalytics';
import BudgetPage from './pages/Budget/BudgetPage';
import VendorsPage from './pages/Vendors/VendorsPage';
import EventsPage from './pages/Events/EventsPage';
import SchedulePage from './pages/Schedule/SchedulePage';
import SeatingPage from './pages/Seating/SeatingPage';
import CountdownPage from './pages/Countdown/CountdownPage';
import MessagesPage from './pages/Messages/MessagesPage';
import MembersPage from './pages/Members/MembersPage';
import SettingsPage from './pages/Settings/SettingsPage';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import AnalyticsPage from './pages/Analytics/AnalyticsPage';
import OnboardingWizard, { isOnboardingDone, markOnboardingDone } from './pages/Onboarding/OnboardingWizard';
import JoinGate from './ui/JoinGate';
import TabBar from './ui/TabBar';
import PermissionDeniedAlert from './ui/PermissionDeniedAlert';
import { readJoinCode, cleanJoinUrl } from './utils/joinCode';

function BootError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <AppRoot>
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Headline level="1" style={{ marginBottom: 12 }}>Не удалось запустить Colibri</Headline>
        <Text style={{ marginBottom: 16, color: 'var(--vkui--color_text_secondary)' }}>{message}</Text>
        <Button size="l" onClick={onRetry}>Повторить</Button>
      </div>
    </AppRoot>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const { weddingsLoaded, loadWeddings } = useWeddingStore();

  // Cold deep-links (/#/guests и др.) не монтируют Dashboard — грузим свадьбы здесь.
  useEffect(() => {
    if (isAuthenticated && !weddingsLoaded) {
      loadWeddings().catch(() => {});
    }
  }, [isAuthenticated, weddingsLoaded, loadWeddings]);

  if (isLoading) {
    return (
      <AppRoot>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Spinner size="l" />
        </div>
      </AppRoot>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { loadUser, isLoading, error, isAuthenticated } = useAuthStore();
  const [bootError, setBootError] = useState<string | null>(null);
  const [joinCode, setJoinCode] = useState<string | null>(null);
  const joinSeenRef = useRef<string | null>(null);
  const [onboardingDone, setOnboardingDone] = useState(() => isOnboardingDone());

  // Код приглашения читаем не только при mount: VK может открыть приложение с новыми
  // query-параметрами поверх уже загруженного WebView (popstate / visibilitychange).
  useEffect(() => {
    const check = () => {
      const code = readJoinCode();
      if (code && code !== joinSeenRef.current) setJoinCode(code);
    };
    check();
    window.addEventListener('popstate', check);
    window.addEventListener('hashchange', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.removeEventListener('popstate', check);
      window.removeEventListener('hashchange', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  useEffect(() => {
    loadUser().catch((e) => {
      console.error('loadUser failed', e);
      setBootError(String(e?.message || e));
    });
  }, []);

  // Handoff-код с лендинга: ВК срезает параметры ссылок, код передаётся через сервер (по IP).
  useEffect(() => {
    if (!isAuthenticated || joinCode) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ invite_code: string | null }>(`weddings/invite-handoff?_t=${Date.now()}`);
        if (!cancelled && res.invite_code && res.invite_code !== joinSeenRef.current) {
          setJoinCode(res.invite_code);
        }
      } catch {
        /* handoff — best effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, joinCode]);

  // Продолжение join после VK ID входа: код приглашения ждал в localStorage
  // (VK ID возвращает на корень сайта, путь /join/... при этом теряется).
  useEffect(() => {
    if (!isAuthenticated || joinCode) return;
    const pending = localStorage.getItem('colibri.pending_join_code');
    if (pending && pending !== joinSeenRef.current) setJoinCode(pending);
  }, [isAuthenticated, joinCode]);

  function handleJoinResolved(result: 'joined' | 'opened' | 'dismissed') {
    if (joinCode) joinSeenRef.current = joinCode;
    localStorage.removeItem('colibri.pending_join_code');
    localStorage.removeItem('colibri.pending_join_auto');
    cleanJoinUrl();
    setJoinCode(null);
    if (result !== 'dismissed') {
      // Уже есть свадьба (своя или присоединённая) — мастер онбординга не нужен
      if (!isOnboardingDone()) markOnboardingDone();
      setOnboardingDone(true);
    }
  }

  if (bootError) {
    return (
      <ConfigProvider>
        <AdaptivityProvider>
          <BootError message={bootError} onRetry={() => window.location.reload()} />
        </AdaptivityProvider>
      </ConfigProvider>
    );
  }

  if (isLoading) {
    return (
      <ConfigProvider>
        <AdaptivityProvider>
          <AppRoot>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <Spinner size="l" />
              {error && (
                <div style={{ color: 'var(--vkui--color_text_negative)', fontSize: 13, maxWidth: 280, textAlign: 'center' }}>
                  {error}
                </div>
              )}
            </div>
          </AppRoot>
        </AdaptivityProvider>
      </ConfigProvider>
    );
  }

  if (joinCode) {
    return (
      <ConfigProvider>
        <AdaptivityProvider>
          <AppRoot>
            <JoinGate code={joinCode} onResolved={handleJoinResolved} />
          </AppRoot>
        </AdaptivityProvider>
      </ConfigProvider>
    );
  }

  if (isAuthenticated && !onboardingDone) {
    return (
      <ConfigProvider>
        <AdaptivityProvider>
          <AppRoot>
            <HashRouter>
              <OnboardingWizard onDone={() => setOnboardingDone(true)} />
            </HashRouter>
          </AppRoot>
        </AdaptivityProvider>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider>
      <AdaptivityProvider>
        <AppRoot>
          <HashRouter>
            <AppShell />
          </HashRouter>
        </AppRoot>
      </AdaptivityProvider>
    </ConfigProvider>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const showTabBar = isAuthenticated && pathname !== '/auth';
  const showBackFab = isAuthenticated && pathname !== '/auth' && pathname !== '/';

  return (
    <div className={showTabBar ? 'colibri-page-with-tabbar' : undefined}>
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/guests" element={<ProtectedRoute><GuestsPage /></ProtectedRoute>} />
        <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
        <Route path="/tasks/analytics" element={<ProtectedRoute><ChecklistAnalytics /></ProtectedRoute>} />
        <Route path="/tasks/:id" element={<ProtectedRoute><TaskDetailPage /></ProtectedRoute>} />
        <Route path="/budget" element={<ProtectedRoute><BudgetPage /></ProtectedRoute>} />
        <Route path="/vendors" element={<ProtectedRoute><VendorsPage /></ProtectedRoute>} />
        <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
        <Route path="/schedule" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
        <Route path="/seating" element={<ProtectedRoute><SeatingPage /></ProtectedRoute>} />
        <Route path="/countdown" element={<ProtectedRoute><CountdownPage /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        <Route path="/members" element={<ProtectedRoute><MembersPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {showBackFab && (
        <button
          type="button"
          aria-label="Назад"
          onClick={() => navigate(-1)}
          style={{
            position: 'fixed',
            right: 12,
            bottom: 'calc(72px + env(safe-area-inset-bottom, 0px) + 12px)',
            zIndex: 90,
            width: 44, height: 44, borderRadius: '50%',
            border: '1px solid var(--vkui--color_separator_primary, rgba(0,0,0,0.12))',
            background: 'var(--vkui--color_background_content, #fff)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--vkui--color_text_primary, #2C2C2E)',
          }}
        >
          <Icon24ChevronLeft />
        </button>
      )}
      {showTabBar && <TabBar />}
      <PermissionDeniedAlert />
    </div>
  );
}
